/* eslint-disable import/prefer-default-export */
// Global helper file for all common methods
import _isEmpty from 'lodash/isEmpty';
import _sortBy from 'lodash/sortBy';

import Page from './page';
import { modalChannels, privacyRefId } from '../contactUs/constants/UtilVariables';
import { BNA, OMNI_CHANNEL_API_SESSION_KEY, pageName } from '../common/commonConstants';

const html = document.querySelector('html');
export const cobrand = html.getAttribute('cobrand') || 'us';
const communityLink = (Page && Page.getCommunityLink()) || '';

/**
 * Check whether a chat/messageUs window is already opened.
 * @returns {boolean}
 */
export const isNodeChatAlreadyExists = (targetNode = null) => {
  let nodeChatElement;
  if (targetNode) {
    nodeChatElement = targetNode;
  } else {
    nodeChatElement = document.getElementById('node-chat') || null;
  }
  if (nodeChatElement) {
    // Old node-chat implementation check
    if (nodeChatElement.style.display) {
      return nodeChatElement.style.display === 'block';
    }

    // New node-chat implementation
    return (nodeChatElement.style.visibility && nodeChatElement.style.visibility === 'visible');
  }
  return false;
};

/**
 * Returns environment
 * @returns {string}
 */
export const getEnvironmentBasedOnLocation = () => {
  const hostName = window.location.hostname;
  if (typeof (hostName) === 'string' && (hostName.includes('localhost') || hostName.includes('.qa.paypal.com'))) {
    return 'staging';
  }
  return 'production';
};

export const defaultNodeChatPollValues = {
  retries: 5,
  delay: 200,
};

/**
 * Returning node chat poll values for chatineligible modal
 * @returns {{retries: number, delay: number}|*}
 */
export const getNodeChatPollValues = () => {
  try {
    const remoteConfigNodeChatPollValues = window && window.nodeChatPoll;
    const parsedConfig = JSON.parse(remoteConfigNodeChatPollValues.replace(/\\/g, ''));
    if (typeof parsedConfig === 'object') {
      const env = getEnvironmentBasedOnLocation();
      if (Object.prototype.hasOwnProperty.call(parsedConfig, env)) {
        return parsedConfig[env];
      }

      return defaultNodeChatPollValues;
    }
    return defaultNodeChatPollValues;
  } catch (e) {
    return defaultNodeChatPollValues;
  }
};

export const getOriginPath = () => `${window.location.protocol}//${window.location.host}`;

export const getModalChannelType = (isChatBotAsyncUser = false) => (isChatBotAsyncUser ? modalChannels.messageUs : modalChannels.chatBot);

/**
 * returns resolution center url
 */
export const getResolutionCenterUrl = () => ('/disputes');

/**
 * returns community url
 */
export const getCommunityExpUrl = () => (communityLink);

/**
 * returns message-center url
 */
export const getMessageCenterUrl = () => ('/smc/customer');

/**
 * When resolution center contact card is clicked on home page opening res-center in new tab
 */
export const openResolutionCenterExp = (contactTarget) => (window.open(`${getOriginPath()}/${cobrand}${getResolutionCenterUrl()}`, contactTarget));

/**
 * When Community contact card is clicked on home page opening community in new tab
 */
export const openCommunityExp = (contactProps) => window.open(`${contactProps.contactUrl}`, contactProps.contactTarget);

/**
 * When Message center contact card is clicked on home page opening message-center in new tab
 */
export const openMessageCenterExp = (contactTarget) => (window.open(`${getOriginPath()}/${cobrand}${getMessageCenterUrl()}`, contactTarget));

/**
 *  Common function to set html content dangerously.
 * @param content
 * @returns {{__html: *}}
 */
export const setInnerContent = (content) => ({ __html: content });

// START OF ---- NAA [ NEVER ACTIVE ACCOUNT ]

export const getNaaConfig = () => {
  try {
    return window && window.naaConfig;
  } catch (e) {
    return null;
  }
};

export const getNaaBnaIds = () => {
  try {
    const naaConfig = getNaaConfig();
    return ((typeof naaConfig === 'object' && naaConfig.bnaIds) || []);
  } catch (e) {
    return [];
  }
};

export const getNaaRedirectUrl = () => {
  const defaultUrl = '/home';
  try {
    const naaConfig = getNaaConfig();
    return ((typeof naaConfig === 'object' && naaConfig.redirectUrl) || defaultUrl);
  } catch (e) {
    return defaultUrl;
  }
};

export const isNaaAlertEnabledInRC = () => {
  try {
    let supportedMarkets = [];
    const country = (Page.getCountry()).toUpperCase();
    const naaConfig = getNaaConfig();
    if (typeof naaConfig === 'object') {
      supportedMarkets = (naaConfig.supportedMarkets || []);
    }
    return (supportedMarkets.indexOf(country) >= 0);
  } catch (e) {
    return false;
  }
};

/**
 * Common function to check in BNA's for Never Active Account type exists or not.
 * @param articles
 * @returns {boolean}
 */
export const isNaaTypeExists = (articles) => {
  if (Array.isArray(articles) && articles.length) {
    const bnaArticles = articles.filter((article) => article.channel === BNA);
    if (bnaArticles.length) {
      const naaBnaIds = getNaaBnaIds();
      return bnaArticles.some((bna) => (bna && bna.channelDetails && bna.channelDetails.bnaId && naaBnaIds.indexOf(bna.channelDetails.bnaId) >= 0));
    }
    return false;
  }
  return false;
};

//  END OF ---- NAA [ NEVER ACTIVE ACCOUNT ]

/**
 * Function to check for contact us page
 */
export const isContactUsPage = () => {
  const pathName = window.location.pathname;
  const contactUsPath = (typeof pathName === 'string' && pathName.split('/').pop());

  if (typeof contactUsPath !== 'string' || !contactUsPath) {
    return false;
  }
  return contactUsPath === pageName.CONTACT_US;
};

// IDI verify is service call is loaded and perform certain actions in the callback function
export const isChannelAvailableInContactOptions = (getState, dispatch, channelName, cbFunction) => {
  const timeOut = window.location.href.includes('localhost') ? 1500 : 1000;

  if (typeof cbFunction !== 'function' || !channelName) {
    return false;
  }

  const isChannelExists = (channels) => {
    if (Array.isArray(channels) && channels.length) {
      const foundChannel = channels.find((channel) => channel.name === channelName.toUpperCase() && channel.availability_decision === 'AVAILABLE');
      return !!(foundChannel && typeof foundChannel === 'object');
    }
    return false;
  };

  // DELAY CHECKER TO KEEP CHECKING FOR IDI CONTACT OPTIONS AND IF SERVICE IS LOADED CHECK REQUIRED CHANNEL IS AVAILABLE IN RISK RESPONSE.
  const delayCheckerForIDIOptions = (retry) => {
    const { contactChannels: { isIdiServiceLoaded, IDIError, OriginalRiskResponse } } = getState();

    if (isIdiServiceLoaded) {
      if (IDIError) {
        return cbFunction(false, dispatch);
      }

      if (isChannelExists(OriginalRiskResponse)) {
        return cbFunction(true, dispatch);
      }
      return cbFunction(false, dispatch);
    }

    if (retry >= 0) {
      return setTimeout(() => delayCheckerForIDIOptions(retry - 1), timeOut);
    }
    return cbFunction(false, dispatch);
  };

  return delayCheckerForIDIOptions(10); // Wait IDI contact options to finish loading
};

/**
 * Remote config for c3merchant migration config
 */
export const getC3MerchantMigrationConfig = () => {
  try {
    if (window && window.C3MerchantMigrationConfig) {
      return window.C3MerchantMigrationConfig;
    }
    return {};
  } catch (e) {
    return {};
  }
};

/**
 *Remote config for C2Merchant migration
 */
export const isC2MerchantMigrated = () => html.getAttribute('isC2MerchatMigrated') === 'true';

/**
 * Returns all the channels avaialble in channelsAvailableForEnabledMarkets in c3merchantMigration remote config
 * @returns {Array|*}
 */
export const returnChannelsFromC3MerchantRC = () => {
  const filterChannelsConfig = getC3MerchantMigrationConfig().channelsAvailableForEnabledMarkets;
  if (Array.isArray(filterChannelsConfig) && filterChannelsConfig.length) {
    // making channels to lowercase
    return filterChannelsConfig.map((channel) => channel.toLowerCase());
  }
  return [];
};

/**
 * checking channel existance in channelsAvailableForEnabledMarkets in c3merchantMigration remote config
 * @returns {boolean}
 */
export const returnChannelExistanceInC3MerchantRC = (channel = '') => {
  const contactChannel = (channel && channel.toLowerCase()) || '';
  const filterChannelsConfig = getC3MerchantMigrationConfig().channelsAvailableForEnabledMarkets;
  if (contactChannel && Array.isArray(filterChannelsConfig) && filterChannelsConfig.length) {
    // making all channels in remote config to lowercase
    const filterChannels = filterChannelsConfig.map((contact) => contact.toLowerCase());
    return filterChannels.indexOf(contactChannel) >= 0;
  }
  return false;
};

/**
 * Checking market existance in enabledMarketsForChannels in c3merchantMigration remote config
 * @returns {boolean}
 */
export const returnMarketExistanceInC3MerchantTRC = () => {
  const filterMarketsConfig = getC3MerchantMigrationConfig().enabledMarketsForChannels;
  if (!_isEmpty(filterMarketsConfig)) {
    const country = (Page.getCountry()).toUpperCase();
    const locale = Page.getAPILocalilty();
    return filterMarketsConfig.split(',')
      .filter((market) => market.indexOf(`${country}:`) >= 0)
      .filter((selectedMarket) => selectedMarket.split(':')[1].indexOf(locale) >= 0)
      .length > 0;
  }

  return false;
};

/**
 * Right now using for C2 MARKET
 * Filter contact channels when market is enabled in c3merchantMigration remoteconfig in loggedout or
 * if the user is isC2MerchantMigrated
 */
export const filterC3MarketsConfig = () => ((!Page.isLoggedIn() && returnMarketExistanceInC3MerchantTRC()) || isC2MerchantMigrated());

/**
 * Checking if market is eligibile and channel is present in c3merchantMigration remote config
 * or is the user is c2merchantmigrated user
 */
export const getC3MarketChannelEligibility = (channel) => {
  // checking for enabledmarkets in c3merchantMigration remote config or if the user is c2merchantmigrated
  if (filterC3MarketsConfig()) {
    // if market is enabled in c3merchantMigration remote config or if the user is C2MerchantMigrated,
    // checking for contact channel in the c3merchantMigration remote config
    return returnChannelExistanceInC3MerchantRC(channel);
  }
  // if user is not c2merchantmigrated and if market is not enabled in c3merchantMigration remoteconfig return true
  return true;
};

/**
 * Disable topic tree when topictreedisabled remoteconfig is true or
 * if the user is isC2MerchantMigrated
 */
export const disableTopicTree = () => (Page.isTopicTreeDisabled() || isC2MerchantMigrated());

/**
 * logged in check Remote config experience values only if user is c2 merchant migrated
 * Logged out check country exists C3MerchantMigrationConfig then return remote config experiences
 * If none of the condition is true, return existing experiences
 * @param existingExperiences
 * @returns {Window.C3MerchantMigrationConfig|{}|*|*}
 */
export const getHomePageExperiences = (existingExperiences) => {
  const isLoggedIn = Page.isLoggedIn();
  const homePageConfigs = getC3MerchantMigrationConfig();

  // C3 Migration Remote config loggedIn experiences
  const loggedInExperiencesEnabled = homePageConfigs && homePageConfigs.loggedInCustomerExperience;

  // In loggedIn if user is isC2MerchantMigrated return experiences in Remote Config
  if (isLoggedIn && isC2MerchantMigrated() && loggedInExperiencesEnabled) {
    return loggedInExperiencesEnabled;
  }

  const loggedOutEnabledExperiences = homePageConfigs.loggedoutCustomerExperience;
  const country = Page.getCountry().toUpperCase();
  const locale = Page.getAPILocalilty();

  // In Logged out if country exists in remote config return remote config experiences
  if (!isLoggedIn && loggedOutEnabledExperiences && loggedOutEnabledExperiences[`${country}|${locale}`]) {
    return loggedOutEnabledExperiences[`${country}|${locale}`];
  }

  // if country doesnot exists in remote config return existing available experiences
  return existingExperiences;
};

/**
 * Remote config for complaints page information
 */
export const getComplaintsPageInfo = () => window.complaintsPageInfo || {};

/**
 * Set OmniChannelAPITriggered value in session to not trigger SET context when update/set context is already triggered
 * @param value
 */
export const omniChannelAPITriggeredInSession = (value = false) => window.sessionStorage && sessionStorage.setItem(OMNI_CHANNEL_API_SESSION_KEY, value);

/**
 * Checking OmniChannelAPITriggered value in session
 */
export const isOmniChannelAPITriggeredInSession = () => window.sessionStorage && sessionStorage.getItem(OMNI_CHANNEL_API_SESSION_KEY) === 'true';

/**
 * Removing OmniChannelAPITriggered value in session
 */
export const removeOmniChannelAPITriggeredInSession = () => window.sessionStorage && sessionStorage.removeItem(OMNI_CHANNEL_API_SESSION_KEY);

/**
 * Function to parse omni channel intents
 */
export const parseOmniChannelIntents = (intents) => {
  if (Array.isArray(intents) && intents.length > 0) {
    // Sorting the intents with score
    const sortIntentsByScore = _sortBy(intents, ['intent', 'score']);
    // Picking the top intent
    const topIntent = sortIntentsByScore[0];
    return [{ customer_intent: topIntent.intent, score: topIntent.score }];
  }
  return [];
};

/**
 * Function which return true or false to validate AM flag from callus response to display callus option
 */
export const validateAMFlag = () => Page.isLoggedIn() && !Page.getIsIDIContactOptions();

/**
 * Filtering Recommended solutions based on the channel type
 */
export const filterRecommendedChannels = (recommendedSolutions, channelType) => {
  if (Array.isArray(recommendedSolutions) && recommendedSolutions.length > 0 && channelType) {
    return recommendedSolutions.filter((article) => article.channel === channelType);
  }
  return [];
};

/**
 * Function to check isNewPrivacyWebFormEnabled remote config is true
 */
export const isNewPrivacyPageEnabled = () => html.hasAttribute('isNewPrivacyWebFormEnabled');

/**
 * For enabling new Privacy webForm
 * Function to check isNewPrivacyWebFormEnabled remote config is true and tCode is PRIVOO1
 */
export const isPrivacyWebFormEnabledInRC = (tCode) => {
  const isPrivacyWebFormEnabled = html.hasAttribute('isNewPrivacyWebFormEnabled');
  return isPrivacyWebFormEnabled && tCode === privacyRefId;
};

/**
 * For enabling Qualtrics Feedback icon
 */
export const isQualtricsFeedbackEnabled = () => {
  const qualticsConfig = (window.qualtricsFeedbackConfig) || {}; // qualtrics config
  const isMarketEnabled = qualticsConfig.isMarketEnabledForQualtrics; // Checking whether market enabled or not
  const pagename = Page.getFPTIPageId();

  // Checking whether market is enabled and of page names existance from qualtrics config
  if (isMarketEnabled && Array.isArray(qualticsConfig.pagesEnabled) && qualticsConfig.pagesEnabled.length > 0) {
    // Checking whether all the pages are enabled
    const isEnabledAllPages = qualticsConfig.pagesEnabled[0].enableAllPages;
    // Checking whether page is enabled
    const isPageEnabled = qualticsConfig.pagesEnabled[0][pagename];

    return !!(isMarketEnabled && (isEnabledAllPages || isPageEnabled));
  }

  return false;
};

/**
 * Function to push ICF Visted articles into window session
 * @returns {string|Array}
 */
export const updateVisitedArticles = (icfId, faqId) => {
  try {
    // getting the articles from session
    let visitedArticlesInSession = (JSON.parse(window.sessionStorage.getItem('icfArticlesVisited')) || []);

    // If visitedArticlesInSession is not empty
    if (Array.isArray(visitedArticlesInSession) && visitedArticlesInSession.length > 0) {
      visitedArticlesInSession = visitedArticlesInSession.map((ele) => {
        // parsing the objects with type string
        if (typeof ele === 'string') {
          return JSON.parse(ele);
        }
        return ele;
      });

      const isExistingICF = visitedArticlesInSession.find((ele) => ele.icfId === icfId);
      // If ICF-id exists into window session
      if (isExistingICF) {
        isExistingICF.accessed.push(faqId);
      } else {
        // else push new icf object
        visitedArticlesInSession.push(JSON.stringify({ icfId, accessed: [faqId] }));
      }
      visitedArticlesInSession = JSON.stringify(visitedArticlesInSession);
      return visitedArticlesInSession;
    }

    // If visitedArticlesInSession is empty, pushing the object into empty array
    const icfClicked = JSON.stringify({ icfId, accessed: [faqId] });
    visitedArticlesInSession.push(icfClicked);
    visitedArticlesInSession = JSON.stringify(visitedArticlesInSession);
    return visitedArticlesInSession;
  } catch (e) {
    return [];
  }
};

/**
 * Function to return icon size
 */
export const iconSize = () => {
  if (Page.isMobileLayout()) {
    return 'sm';
  } if (Page.isTabletLayout()) {
    return 'md';
  }
  return 'lg';
};

/**
 * Function checks whether vht widget is enabled and returns widget id
 */
export const getVhtWidgetId = () => {
  const vhtConfig = window.vhtConfig || {};
  if (!_isEmpty(vhtConfig)) {
    return vhtConfig.widgetId;
  }
  return '';
};

/**
 * Function to parse HTML Entities
 */
export const parseHtmlEntities = (str) => {
  try {
    return String(str).replace(/&lt;/g, '<').replace(/&gt;/g, '>');
  } catch (e) {
    return str;
  }
};
